/* eslint-disable camelcase */
import React, { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import isEmpty from 'lodash/isEmpty'
import update from 'immutability-helper'
import { DndProvider, useDrop } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import TouchBackend from 'react-dnd-touch-backend'

import {
  Button,
  Typography,
  File,
  ImageDropzone,
  TextButton,
  DeleteIcon,
  Link,
  Select,
} from '~/legacy/components'
import { uploadFilesFromPdf, getPlural } from '~/legacy/utils'
import { fetchApi } from '~/legacy/fetchApi/rootApi'
import { SelectableDraggableImage } from './SelectableDraggableImage'

const IMAGE_CATEGORY_KEYS = {
  BUILDING: 1,
  SPACE: 2,
  FLOORPLAN: 3,
}

const IMAGE_CATEGORIES_DATA = [
  {
    key: IMAGE_CATEGORY_KEYS.BUILDING,
    endpoint: 'building_photos',
    imagesKey: 'images',
    label: 'Building',
    parentObject: 'building',
    emptyStateText: 'Upload Building Photos',
  },
  {
    key: IMAGE_CATEGORY_KEYS.SPACE,
    endpoint: 'listing_photos',
    imagesKey: 'images',
    label: 'Space',
    parentObject: 'listing',
    emptyStateText: 'Upload Space Photos',
  },
  {
    key: IMAGE_CATEGORY_KEYS.FLOORPLAN,
    endpoint: 'listing_floorplans',
    imagesKey: 'floorplan_photos',
    label: 'Floor Plan',
    parentObject: 'listing',
    emptyStateText: 'Upload Floorplan Photos',
  },
]

// Determine if touch device for DnD backend
const isTouchDevice = () => {
  if ('ontouchstart' in window) {
    return true
  }
  return false
}
const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend

const useStyles = makeStyles(() => ({
  closeIcon: {
    cursor: 'pointer',
    margin: '0 24px 0 auto',
    color: '#111111',
  },
  content: {
    backgroundColor: 'white',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    width: '95vw',
    maxWidth: '1280px',
    minHeight: '540px',
  },
  footer: {
    padding: '24px',
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    zIndex: 10,
    borderTop: '1px solid #e0e0e0',
  },
  header: {
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    height: '60px',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10,
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    height: '60px',
  },
  selectionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectionActionButton: {
    color: '#111111',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: '#1E847A',
    },
  },
  nameTitle: {
    display: 'flex',
    marginLeft: '24px',
    flex: 1,
    color: '#111111',
    '&:hover': {
      color: '#1E847A',
    },
  },
  closeIconContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'end',
  },
  saveButton: {
    marginLeft: '12px',
  },
  sectionContainer: {
    marginBottom: '34px',
  },
  sectionTitle: {
    marginBottom: '24px',
  },
  emptySectionContainer: {
    marginBottom: '34px',
    marginTop: '24px',
  },

  categoryContainer: {
    borderBottom: '1px solid #e0e0e0',
    margin: '0px 30px 34px',
    scrollMarginTop: '120px',
  },
  buildingCategoryContainer: {
    margin: '0px  34px',
    scrollMarginTop: '120px',
  },
  categoryTitle: {
    marginBottom: '24',
  },
  imagesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  emptySection: {
    marginTop: '24px',
    minHeight: '200px',
  },
  spacesNavigation: {
    padding: '24px',
    marginBottom: '34px',
    display: 'flex',
    borderBottom: '1px solid #e0e0e0',
    borderTop: '1px solid #e0e0e0',
    scrollMarginTop: '120px',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 10,
  },
  spacesNavigationLink: {
    marginRight: '24px',
    backgroundColor: '#f3f3f3',
    padding: '4px 12px',
    borderRadius: '16px',
    color: '#111111',
  },
  bulkActionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 32px',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderBottom: '1px solid #e0e0e0',
    position: 'sticky',
    top: 60,
    zIndex: 10,
    marginBottom: '12px',
  },
  trashCanIcon: {
    marginRight: '4px',
  },
  imageDropzone: {
    backgroundColor: 'white',
    border: '1px dashed #e0e0e0',
  },
  moveToSelect: {
    width: '200px',
    marginRight: '8px',
  },
}))

const ImagesSection = ({
  imageCategory,
  listingIndex = null,
  listingId = null,
  imageLists,
  classes,
  moveImageInCategory,
  removeImage,
  moveCrossCategory,
  selectedImages,
  toggleImageSelection,
  handleFileUpload,
  updateFile,
}) => {
  let imageList
  let imageKey

  if (imageCategory.parentObject === 'building') {
    const buildingImageKey =
      imageCategory.key === IMAGE_CATEGORY_KEYS.BUILDING
        ? 'building_images'
        : 'building_neighborhood_photos'

    imageList = imageLists[buildingImageKey] || []
    imageKey = buildingImageKey
  }

  if (imageCategory.parentObject === 'listing') {
    const listingImageKey =
      imageCategory.key === IMAGE_CATEGORY_KEYS.SPACE
        ? `listing_${listingIndex}_images`
        : `listing_${listingIndex}_floorplan_photos`

    imageList = imageLists[listingImageKey] || []
    imageKey = listingImageKey
  }

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'image',
    drop: (item) => {
      if (item.imageKey !== imageKey) {
        moveCrossCategory(item.imageKey, imageKey, item.index, 0)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      draggedItem: monitor.getItem(),
    }),
  })

  const isActive = isOver && canDrop

  if (!isEmpty(imageList)) {
    return (
      <div
        key={imageKey}
        className={classes.sectionContainer}
        id={`listing-${listingId}`}
      >
        <div className={classes.imagesContainer}>
          {imageList.map((image, idx) => (
            <SelectableDraggableImage
              key={image.uuid}
              index={idx}
              imageKey={imageKey}
              moveImage={moveImageInCategory}
              removeImage={removeImage}
              moveCrossCategory={moveCrossCategory}
              isSelected={selectedImages.has(image.uuid)}
              selectedImages={selectedImages}
              toggleSelection={(event) =>
                toggleImageSelection(image.uuid, event)
              }
            >
              <File
                endpoint={imageCategory.endpoint}
                file={image.data}
                skipUploading={image.skipUpload || false}
                updateFile={(newFileData) =>
                  updateFile(image, newFileData, imageKey)
                }
                showCancelButton={false}
              />
            </SelectableDraggableImage>
          ))}
          <ImageDropzone
            isSmall
            onFileDrop={handleFileUpload(imageKey, imageCategory)}
          />
        </div>
      </div>
    )
  }
  return (
    <div
      ref={drop}
      key={imageKey}
      className={classes.emptySectionContainer}
      id={`listing-${listingId}`}
      style={{
        backgroundColor: isActive ? '#F3F3F3' : 'transparent',
        transition: 'background-color 0.2s ease',
      }}
    >
      <ImageDropzone
        onFileDrop={handleFileUpload(imageKey, imageCategory)}
        overrideClasses={classes.imageDropzone}
        headerText={isActive ? 'Drop images here' : 'Upload images'}
      />
    </div>
  )
}

export function AllinOneImageUploadModal({
  onClose,
  modalHeader,
  surveyBuilding,
  updateBuilding,
  updateListing,
  refreshData,
}) {
  const classes = useStyles()
  const { building, survey_listings } = surveyBuilding || {}
  const listings = survey_listings?.map((sl) => sl.listing) || []
  const [hasLocalChanges, setHasLocalChanges] = useState(false)
  const [selectedImages, setSelectedImages] = useState(new Set())
  const [lastSelectedImage, setLastSelectedImage] = useState(null)
  const [isSaveLoading, setIsSaveLoading] = useState(false)

  // Initialize image lists for all categories
  const [imageLists, setImageLists] = useState(() => {
    const allImageLists = {}
    const buildingImages = building.images || []

    allImageLists.building_images = buildingImages.map((image) => ({
      uuid: image.uuid,
      data: image,
    }))

    const neighborhoodImages = building.neighborhood_photos || []
    allImageLists.building_neighborhood_photos = neighborhoodImages.map(
      (image) => ({
        uuid: image.uuid,
        data: image,
      })
    )

    if (listings && listings.length > 0) {
      listings.forEach((listing, index) => {
        const spaceImages = listing.images || []
        const spaceKey = `listing_${index}_images`
        allImageLists[spaceKey] = spaceImages.map((image) => ({
          uuid: image.uuid,
          data: image,
          listingId: listing.id,
        }))

        const floorplanImages = listing.floorplan_photos || []
        const floorplanKey = `listing_${index}_floorplan_photos`
        allImageLists[floorplanKey] = floorplanImages.map((image) => ({
          uuid: image.uuid,
          data: image,
          listingId: listing.id,
        }))
      })
    }

    return allImageLists
  })

  const toggleImageSelection = (uuid, event = null) => {
    if (event?.shiftKey && lastSelectedImage) {
      selectImageRange(lastSelectedImage, uuid)
    } else {
      // Handle normal click
      setSelectedImages((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(uuid)) {
          newSet.delete(uuid)
        } else {
          newSet.add(uuid)
          setLastSelectedImage(uuid)
        }
        return newSet
      })
    }
  }

  const selectImageRange = (startUuid, endUuid) => {
    // Get all images in order across all categories
    const allImages = Object.entries(imageLists).flatMap(([imageKey, images]) =>
      images.map((image, index) => ({ uuid: image.uuid, imageKey, index }))
    )

    const startIndex = allImages.findIndex((img) => img.uuid === startUuid)
    const endIndex = allImages.findIndex((img) => img.uuid === endUuid)

    if (startIndex !== -1 && endIndex !== -1) {
      const [min, max] = [
        Math.min(startIndex, endIndex),
        Math.max(startIndex, endIndex),
      ]
      const rangeUuids = allImages.slice(min, max + 1).map((img) => img.uuid)

      setSelectedImages((prev) => {
        const newSet = new Set(prev)
        rangeUuids.forEach((uuid) => newSet.add(uuid))
        return newSet
      })
    }
  }

  const deleteSelectedImages = () => {
    setHasLocalChanges(true)
    setImageLists((oldLists) => {
      const updatedLists = { ...oldLists }

      Object.keys(updatedLists).forEach((imageKey) => {
        updatedLists[imageKey] = updatedLists[imageKey].filter(
          (image) => !selectedImages.has(image.uuid)
        )
      })

      return updatedLists
    })

    setSelectedImages(new Set())
  }

  const clearSelection = () => {
    setSelectedImages(new Set())
  }

  const moveSelectedImages = (targetImageKey, targetIndex = 0) => {
    if (selectedImages.size === 0) return

    setHasLocalChanges(true)
    const selectedUuids = Array.from(selectedImages)

    setImageLists((oldLists) => {
      const updatedLists = { ...oldLists }
      const imagesToMove = []

      // Collect all selected images from all categories
      Object.entries(updatedLists).forEach(([sourceKey, images]) => {
        images.forEach((image, index) => {
          if (selectedUuids.includes(image.uuid)) {
            imagesToMove.push({
              ...image,
              sourceKey,
              sourceIndex: index,
            })
          }
        })
      })

      // Remove selected images from their current locations
      Object.keys(updatedLists).forEach((imageKey) => {
        updatedLists[imageKey] = updatedLists[imageKey].filter(
          (image) => !selectedUuids.includes(image.uuid)
        )
      })

      // Add metadata based on target category
      const processedImages = imagesToMove.map((image) => {
        const cleanedImage = {
          uuid: image.uuid,
          data: image.data,
          skipUpload: image.skipUpload,
          needsConversion: !!image.data?.id,
          originalImageKey: image.sourceKey,
        }

        if (targetImageKey.includes('listing_')) {
          const targetListingIndex = parseInt(targetImageKey.split('_')[1], 10)
          cleanedImage.listingId = listings[targetListingIndex]?.id
        }

        return cleanedImage
      })

      // Add to target category
      const targetList = updatedLists[targetImageKey] || []
      updatedLists[targetImageKey] = [
        ...targetList.slice(0, targetIndex),
        ...processedImages,
        ...targetList.slice(targetIndex),
      ]

      return updatedLists
    })

    setSelectedImages(new Set())
  }

  const handleFileUpload = (imageKey, imageCategory) => (filesIn) => {
    setHasLocalChanges(true)
    const newFiles = []
    filesIn.forEach((fileIn) => {
      if (fileIn.type === 'application/pdf') {
        const newId = uuidv4()
        uploadFilesFromPdf(
          fileIn,
          imageCategory.endpoint,
          (singleUploadResult) => {
            setImageLists((oldLists) => {
              const updatedList = {
                ...oldLists,
                [imageKey]: (oldLists[imageKey] || [])
                  .filter((i) => i.uuid !== newId)
                  .concat({
                    uuid: singleUploadResult.data.file.uuid,
                    data: singleUploadResult.data.file,
                    ...(imageKey.includes('listing_')
                      ? { listingId: parseInt(imageKey.split('_')[1], 10) }
                      : {}),
                  }),
              }
              return updatedList
            })
          }
        )
        newFiles.push({
          uuid: newId,
          skipUpload: true,
          data: fileIn,
        })
      } else {
        newFiles.push({
          uuid: uuidv4(),
          data: fileIn,
          ...(imageKey.includes('listing_')
            ? { listingId: parseInt(imageKey.split('_')[1], 10) }
            : {}),
        })
      }
    })

    setImageLists((oldLists) => {
      const currentList = oldLists[imageKey] || []
      return {
        ...oldLists,
        [imageKey]: [...currentList, ...newFiles],
      }
    })
  }

  const moveImageInCategory = (dragIndex, hoverIndex, imageKey) => {
    setHasLocalChanges(true)
    setImageLists((oldLists) => {
      const currentList = oldLists[imageKey] || []
      return {
        ...oldLists,
        [imageKey]: update(currentList, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, currentList[dragIndex]],
          ],
        }),
      }
    })
  }

  const removeImage = (index, imageKey) => {
    setHasLocalChanges(true)
    setImageLists((oldLists) => {
      const currentList = oldLists[imageKey] || []
      return {
        ...oldLists,
        [imageKey]: update(currentList, {
          $splice: [[index, 1]],
        }),
      }
    })
  }

  const updateFile = (image, newFileData, imageKey) => {
    setImageLists((oldLists) => {
      const currentList = oldLists[imageKey] || []
      return {
        ...oldLists,
        [imageKey]: currentList
          .filter((i) => i.uuid !== image.uuid)
          .concat([
            {
              ...image,
              data: newFileData,
            },
          ]),
      }
    })
  }

  const moveCrossCategory = (
    sourceImageKey,
    targetImageKey,
    sourceIndex,
    targetIndex
  ) => {
    setImageLists((oldLists) => {
      const sourceList = oldLists[sourceImageKey] || []
      const targetList = oldLists[targetImageKey] || []

      const imageToMove = sourceList[sourceIndex]
      if (!imageToMove) return oldLists

      // Remove from source
      const newSourceList = sourceList.filter(
        (_, index) => index !== sourceIndex
      )

      // Mark image for conversion if it has a valid id
      const cleanedImage = {
        uuid: imageToMove.uuid,
        data: imageToMove.data,
        skipUpload: imageToMove.skipUpload,
        needsConversion: !!imageToMove.data?.id,
        originalImageKey: sourceImageKey,
      }

      // Add category-specific metadata based on TARGET, not source
      if (targetImageKey.includes('listing_')) {
        const targetListingIndex = parseInt(targetImageKey.split('_')[1], 10)
        cleanedImage.listingId = listings[targetListingIndex]?.id
      }
      // If moving to building category, remove any existing listingId
      else {
        delete cleanedImage.listingId
      }

      // Add to target at specified position
      const newTargetList = [...targetList]
      newTargetList.splice(targetIndex, 0, cleanedImage)

      return {
        ...oldLists,
        [sourceImageKey]: newSourceList,
        [targetImageKey]: newTargetList,
      }
    })
  }

  const movePhotos = async (images) => {
    try {
      const [response, data] = await fetchApi({
        endpoint: 'move_photo/',
        fetchArgs: {
          method: 'POST',
          body: JSON.stringify({
            images,
          }),
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to move photos: ${response.statusText}`)
      }

      return data
    } catch (error) {
      console.error('Error moving photos:', error)
      throw error
    }
  }

  const processImageConversions = async (imageLists) => {
    const convertedImageLists = { ...imageLists }
    const conversionsToProcess = []

    // Helper to map imageKey to source_type
    const getSourceType = (imageKey) => {
      if (imageKey.includes('floorplan_photos')) {
        return 'listing_floorplan'
      } if (
        imageKey.includes('listing_') &&
        imageKey.includes('_images')
      ) {
        return 'listing'
      } if (
        imageKey === 'building_images' ||
        imageKey === 'building_neighborhood_photos'
      ) {
        return 'building'
      }
      return 'building' // fallback
    }

    Object.entries(convertedImageLists).forEach(([imageKey, images]) => {
      images.forEach((image, index) => {
        if (image.needsConversion && image.data?.uuid) {
          let targetType
          let targetId

          if (imageKey.includes('floorplan_photos')) {
            targetType = 'listing_floorplan'
            targetId = image.listingId
          } else if (
            imageKey.includes('listing_') &&
            imageKey.includes('_images')
          ) {
            targetType = 'listing'
            targetId = image.listingId
          } else if (
            imageKey === 'building_images' ||
            imageKey === 'building_neighborhood_photos'
          ) {
            targetType = 'building'
            targetId = building?.id
          } else {
            console.error('Unknown image key format:', imageKey)
            return
          }

          // Use originalImageKey to determine source_type, not current location
          const originalImageKey = image.originalImageKey || imageKey
          const sourceType = getSourceType(originalImageKey)

          conversionsToProcess.push({
            imageKey,
            index,
            image,
            source_id: image.data?.id, // Store source_id for matching results
            source_type: sourceType,
            target_type: targetType,
            target_id: targetId,
            order: index,
          })
        }
      })
    })

    if (conversionsToProcess.length === 0) {
      return convertedImageLists
    }

    try {
      const batchPayload = conversionsToProcess.map(
        ({ source_type, target_type, target_id, order, image }) => ({
          source_id: image?.data?.id, // Use photo ID to identify specific record
          source_type, // Specify source table to avoid wrong table lookup
          target_type,
          target_id,
          order,
        })
      )

      const results = await movePhotos(batchPayload)

      // Apply successful conversions back to the image lists
      // Match by source_id instead of index (results may not be in same order)
      conversionsToProcess.forEach((conversion) => {
        const result = results?.results?.find(
          (r) => r.source_id === conversion.source_id
        )
        const error = results?.errors?.find(
          (e) => e.source_id === conversion.source_id
        )

        if (result && result.success) {
          // Update the target image
          convertedImageLists[conversion.imageKey][conversion.index] = {
            ...conversion.image,
            data: {
              ...conversion.image.data,
              id: result.id,
              uuid: result.uuid,
            },
            needsConversion: false,
            skipUpload: true,
          }

          // Remove from original source if it was moved
          if (
            conversion.image.originalImageKey &&
            conversion.image.originalImageKey !== conversion.imageKey
          ) {
            convertedImageLists[conversion.image.originalImageKey] =
              convertedImageLists[conversion.image.originalImageKey].filter(
                (img) => img.uuid !== conversion.image.uuid
              )
          }
        } else {
          // Handle error - log and potentially revert
          const errorMessage = error?.error || result?.error || 'Unknown error'
          console.error('Failed to convert photo:', errorMessage)
          
          // If conversion failed, revert image to original location
          // This prevents data loss (image would be filtered out during save)
          if (conversion.image.originalImageKey) {
            const revertedImage = {
              ...conversion.image,
              needsConversion: false,
              originalImageKey: undefined,
              skipUpload: true,
            }
            
            // Remove from failed target location
            convertedImageLists[conversion.imageKey] =
              convertedImageLists[conversion.imageKey].filter(
                (img, idx) => idx !== conversion.index
              )
            
            // Restore to original location
            if (!convertedImageLists[conversion.image.originalImageKey]) {
              convertedImageLists[conversion.image.originalImageKey] = []
            }
            convertedImageLists[conversion.image.originalImageKey].push(
              revertedImage
            )
          }
        }
      })
    } catch (error) {
      console.error('Batch conversion failed:', error)
    }

    return convertedImageLists
  }

  const updateBuildingAndListings = async (newListingData) => {
    // Update building data if present
    if (newListingData.building) {
      const newBuildingPartial = {
        images: newListingData.building.images || [],
        neighborhood_photos: newListingData.building.neighborhood_photos || [],
      }
      await updateBuilding(newBuildingPartial)
    }

    // Update all listings/spaces if present
    if (newListingData.listings && newListingData.listings.length > 0) {
      await Promise.all(
        newListingData.listings.map(async (updatedListing) => {
          const newSpacePartial = {
            images: updatedListing.images || [],
            floorplan_photos: updatedListing.floorplan_photos || [],
          }
          return updateListing(updatedListing.id, newSpacePartial)
        })
      )
    }
  }

  const saveAndClose = async () => {
    setIsSaveLoading(true)
    // Handle cross-category moves first
    const convertedImageLists = await processImageConversions(imageLists)

    // Only do additional save if we have local changes (reordering, uploads, deletes)
    if (hasLocalChanges) {
      const imagesToSave = {}
      Object.entries(convertedImageLists).forEach(([key, images]) => {
        const imagesWithoutConversion = images.filter(
          (image) => !image.needsConversion
        )
        if (imagesWithoutConversion.length > 0) {
          imagesToSave[key] = imagesWithoutConversion
        }
      })

      if (Object.keys(imagesToSave).length > 0) {
        const buildingSafe = building || {}
        const formattedImages = {
          building: {
            ...buildingSafe,
            images: (imagesToSave.building_images || []).map((item) =>
              item.data?.id ? { id: item.data.id } : item.data
            ),
            neighborhood_photos: (
              imagesToSave.building_neighborhood_photos || []
            ).map((item) => (item.data?.id ? { id: item.data.id } : item.data)),
          },
          listings: listings.map((listing, index) => {
            const spaceKey = `listing_${index}_images`
            const floorplanKey = `listing_${index}_floorplan_photos`

            return {
              id: listing.id,
              images: (imagesToSave[spaceKey] || []).map((item) =>
                item.data?.id ? { id: item.data.id } : item.data
              ),
              floorplan_photos: (imagesToSave[floorplanKey] || []).map((item) =>
                item.data?.id ? { id: item.data.id } : item.data
              ),
            }
          }),
        }
        await updateBuildingAndListings(formattedImages)
      }
    }

    await refreshData()
    setIsSaveLoading(false)
    onClose()
  }

  return (
    <div className={classes.content}>
      <div className={classes.header}>
        <Typography variant="boldText" className={classes.nameTitle}>
          {modalHeader}
        </Typography>
        <div className={classes.navigation}>
          <Link
            onClick={() => {
              document.getElementById('modal-top').scrollIntoView({
                behavior: 'auto',
              })
            }}
          >
            <Typography variant="boldText" className={classes.nameTitle}>
              Building
            </Typography>
          </Link>
          <Link
            onClick={() => {
              document.getElementById('spaces-section').scrollIntoView({
                behavior: 'auto',
              })
            }}
          >
            <Typography variant="boldText" className={classes.nameTitle}>
              Spaces
            </Typography>
          </Link>
        </div>
        <div className={classes.closeIconContainer}>
          <CloseIcon className={classes.closeIcon} onClick={saveAndClose} />
        </div>
      </div>

      <div
        className={classes.bulkActionBar}
        style={
          selectedImages.size > 0
            ? { visibility: 'visible' }
            : { visibility: 'hidden' }
        }
      >
        <Typography variant="bodyBold">
          {getPlural(selectedImages.size, 'image')}
          {' '}
selected
        </Typography>
        <div className={classes.selectionActions}>
          <Select
            label="Move to..."
            value=""
            onChange={(e) => {
              if (e.target.value) {
                moveSelectedImages(e.target.value, 0)
              }
            }}
            options={[
              { value: 'building_images', label: 'Building Photos' },
              ...listings.flatMap((listing, index) => {
                const listingName =
                  listing.address2 || listing.name || `Space ${index + 1}`
                return [
                  {
                    value: `listing_${index}_images`,
                    label: `${listingName} - Space`,
                  },
                  {
                    value: `listing_${index}_floorplan_photos`,
                    label: `${listingName} - Floorplan`,
                  },
                ]
              }),
            ]}
            showNone={false}
            required={false}
            className={classes.moveToSelect}
          />

          <Link
            onClick={clearSelection}
            className={classes.selectionActionButton}
          >
            <Typography variant="boldText">Clear Selection</Typography>
          </Link>

          <Link
            onClick={deleteSelectedImages}
            className={classes.selectionActionButton}
          >
            <DeleteIcon className={classes.trashCanIcon} />
            <Typography variant="boldText">Delete</Typography>
          </Link>
        </div>
      </div>
      <DndProvider backend={backendForDND}>
        {/* Render Building and Neighborhood sections first */}
        <div className={classes.buildingCategoryContainer} id="modal-top">
          <Typography variant="pageSubTitle">Building</Typography>
          {IMAGE_CATEGORIES_DATA.filter(
            (category) => category.parentObject === 'building'
          ).map((categoryData) => (
            <ImagesSection
              imageCategory={categoryData}
              key={`${categoryData.key}-imageSection`}
              imageLists={imageLists}
              classes={classes}
              moveImageInCategory={moveImageInCategory}
              removeImage={removeImage}
              moveCrossCategory={moveCrossCategory}
              selectedImages={selectedImages}
              toggleImageSelection={toggleImageSelection}
              handleFileUpload={handleFileUpload}
              updateFile={updateFile}
              moveSelectedImages={moveSelectedImages}
            />
          ))}
        </div>
        <div
          className={classes.spacesNavigation}
          id="spaces-section"
          style={
            selectedImages.size > 0
              ? { top: 133 }
              : {
                  top: 59,
                }
          }
        >
          {listings &&
            listings.length > 0 &&
            listings.map((listing, index) => {
              const listingName =
                listing.address2 || listing.name || `Space ${index + 1}`
              return (
                <TextButton
                  onClick={() => {
                    document
                      .getElementById(`listing-${listing.id}`)
                      .scrollIntoView({
                        behavior: 'auto',
                      })
                  }}
                  key={`${listing.id}-navigation`}
                  className={classes.spacesNavigationLink}
                >
                  <Typography variant="bodyBold">{listingName}</Typography>
                </TextButton>
              )
            })}
        </div>
        {/* Render Space and Floorplan sections grouped by listing */}
        {listings &&
          listings.length > 0 &&
          listings.map((listing, index) => {
            const listingName =
              listing.address2 || listing.name || `Space ${index + 1}`
            return (
              <div
                key={`listing-${listing.id}`}
                id={`listing-${listing.id}`}
                className={classes.categoryContainer}
                style={
                  index === listings.length - 1 ? { borderBottom: 'none' } : {}
                }
              >
                <Typography
                  variant="pageSubTitle"
                  className={classes.sectionTitle}
                >
                  {listingName}
                </Typography>
                <Typography
                  variant="textSmall"
                  className={classes.categoryTitle}
                >
                  SPACE
                </Typography>
                <ImagesSection
                  imageCategory={IMAGE_CATEGORIES_DATA.find(
                    (category) => category.key === IMAGE_CATEGORY_KEYS.SPACE
                  )}
                  listingIndex={index}
                  listingId={listing.id}
                  imageLists={imageLists}
                  classes={classes}
                  moveImageInCategory={moveImageInCategory}
                  removeImage={removeImage}
                  moveCrossCategory={moveCrossCategory}
                  selectedImages={selectedImages}
                  toggleImageSelection={toggleImageSelection}
                  handleFileUpload={handleFileUpload}
                  updateFile={updateFile}
                  moveSelectedImages={moveSelectedImages}
                />
                <Typography
                  variant="textSmall"
                  className={classes.categoryTitle}
                >
                  FLOORPLAN
                </Typography>
                <ImagesSection
                  imageCategory={IMAGE_CATEGORIES_DATA.find(
                    (category) => category.key === IMAGE_CATEGORY_KEYS.FLOORPLAN
                  )}
                  listingIndex={index}
                  imageLists={imageLists}
                  classes={classes}
                  moveImageInCategory={moveImageInCategory}
                  removeImage={removeImage}
                  moveCrossCategory={moveCrossCategory}
                  selectedImages={selectedImages}
                  toggleImageSelection={toggleImageSelection}
                  handleFileUpload={handleFileUpload}
                  updateFile={updateFile}
                  moveSelectedImages={moveSelectedImages}
                />
              </div>
            )
          })}
      </DndProvider>

      <div className={classes.footer}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          className={classes.saveButton}
          color="primary"
          onClick={saveAndClose}
          disabled={isSaveLoading}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
